/**
 * ContractIntake — Phase 1 game-changer.
 * Drop PDF → IA extrait tout → user review + valide → contrat créé.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Sparkles, ShieldAlert, ShieldCheck, FileText, ArrowLeft,
  CheckCircle2, AlertTriangle, Loader2, Building2, Calendar, Euro,
  Repeat, ScrollText, Paperclip, X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const CYCLE_OPTIONS = [
  { value: "mensuel", label: "Mensuel" },
  { value: "trimestriel", label: "Trimestriel" },
  { value: "semestriel", label: "Semestriel" },
  { value: "annuel", label: "Annuel" },
  { value: "ponctuel", label: "Ponctuel" },
];
const CYCLE_LEGACY = {
  monthly: "mensuel", quarterly: "trimestriel", semi_annual: "semestriel",
  annual: "annuel", yearly: "annuel", one_shot: "ponctuel",
};
const normCycle = (v) => CYCLE_LEGACY[v] || v || "mensuel";

const RISK_META = {
  clause_abusive_l442: { label: "Clause abusive (L442-1)", tone: "high" },
  reconduction_chatel: { label: "Reconduction tacite non conforme (Chatel)", tone: "medium" },
  rgpd_dpa_manquant: { label: "DPA RGPD manquant", tone: "high" },
  non_concurrence_sans_indemnite: { label: "Non-concurrence sans indemnité", tone: "high" },
  penalites_disproportionnees: { label: "Pénalités disproportionnées", tone: "medium" },
  resiliation_unilaterale: { label: "Résiliation unilatérale sans motif", tone: "medium" },
  exclusivite_excessive: { label: "Exclusivité excessive", tone: "medium" },
  prix_indeterme: { label: "Prix indéterminé", tone: "high" },
};
const TYPE_LABEL = {
  bail_commercial: "Bail commercial", bail_habitation: "Bail habitation",
  prestation_saas: "Prestation SaaS", prestation_service: "Prestation service",
  cdi: "CDI", cdd: "CDD", freelance: "Freelance", nda: "NDA",
  contrat_cadre: "Contrat cadre", assurance: "Assurance",
  location_vehicule: "Location véhicule", maintenance: "Maintenance",
  sponsoring: "Sponsoring", distribution: "Distribution", autre: "Autre",
};

export default function ContractIntake() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [annexes, setAnnexes] = useState([]); // Array<File>
  const [drag, setDrag] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle|ready|uploading|extracted|saving
  const [result, setResult] = useState(null);
  const [ext, setExt] = useState(null); // extracted (editable)
  const inputRef = useRef();
  const annexInputRef = useRef();

  const isSupportedFile = (f) => {
    if (!f) return false;
    return f.type.match(/pdf|image/) || f.name.endsWith(".pdf");
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!isSupportedFile(f)) {
      toast.error("Format non supporté (PDF, JPG, PNG uniquement)"); return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (25 MB max)"); return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setPhase("ready");
  };

  const addAnnexes = (files) => {
    const arr = Array.from(files || []);
    const valid = arr.filter((f) => {
      if (!isSupportedFile(f)) {
        toast.error(`${f.name} : format non supporté`); return false;
      }
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name} : trop volumineux (15 MB max par annexe)`); return false;
      }
      return true;
    });
    setAnnexes((prev) => [...prev, ...valid]);
  };

  const removeAnnex = (idx) => {
    setAnnexes((prev) => prev.filter((_, i) => i !== idx));
  };

  const analyze = async () => {
    if (!file) return;
    setPhase("uploading");
    const fd = new FormData();
    fd.append("file", file);
    annexes.forEach((a) => fd.append("attachments", a));
    try {
      const { data } = await api.post("/contracts/intake", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      // Sécurité anti-anglais : normalise le cycle côté client si l'IA
      // renvoie encore "monthly" (defensive).
      if (data.extracted) {
        data.extracted.billing_cycle = normCycle(data.extracted.billing_cycle);
      }
      setResult(data);
      setExt(data.extracted);
      setPhase("extracted");
      const annexCount = data.attachment_ids?.length || 0;
      toast.success(
        `Contrat analysé en ${data.model}${annexCount ? ` · ${annexCount} annexe(s) traitée(s)` : ""}`
      );
    } catch (e) {
      toast.error(apiError(e));
      setPhase("ready");
    }
  };

  const save = async () => {
    setPhase("saving");
    try {
      const { data } = await api.post("/contracts/intake/save", {
        file_asset_id: result.file_asset_id,
        attachment_ids: result.attachment_ids || [],
        extracted: ext,
        model: result.model,
      });
      toast.success("Contrat créé avec succès");
      navigate(`/app/contracts/${data.id}`);
    } catch (e) { toast.error(apiError(e)); setPhase("extracted"); }
  };

  const reset = () => {
    setFile(null); setPreviewUrl(null); setAnnexes([]);
    setResult(null); setExt(null); setPhase("idle");
  };

  return (
    <div className="space-y-5" data-testid="contract-intake-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Importer un contrat (PDF)
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Dépose ton PDF — l'IA extrait automatiquement toutes les informations (parties, dates, montants, clauses, risques) et crée le contrat en 30 secondes.
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate("/app/contracts/annuaire")}
                data-testid="intake-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={2} />
          Retour aux contrats
        </Button>
      </div>

      {/* ── ZONE INTAKE ── */}
      {(phase === "idle" || phase === "ready") && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-slate-200">
            {/* Contrat principal — dropzone */}
            <div
              onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
              onClick={() => !file && inputRef.current?.click()}
              className={cn(
                "rounded-xl border-2 border-dashed p-8 text-center transition-all",
                !file && "cursor-pointer",
                drag ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
              )}
              data-testid="intake-dropzone"
            >
              {!file ? (
                <>
                  <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <Upload className="h-6 w-6 text-blue-600" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    1️⃣ Dépose le contrat PDF ici
                  </h3>
                  <p className="text-xs text-slate-500 mb-2">
                    Ou clique pour parcourir · PDF, JPG, PNG · 25 MB max
                  </p>
                  <div className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                    <Sparkles className="h-3 w-3" /> Analyse Gemini 2.5 · ~15 secondes
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-3 text-left" data-testid="intake-main-file-selected">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
                    <div className="text-xs text-slate-500">
                      Contrat principal · {(file.size / 1024).toFixed(0)} Ko
                    </div>
                  </div>
                  <Button variant="ghost" size="sm"
                          onClick={(e) => { e.stopPropagation(); reset(); }}
                          data-testid="intake-remove-main">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <input id="intake-file-input" name="intake-file-input" type="file" ref={inputRef} className="hidden"
                     accept=".pdf,image/*"
                     onChange={(e) => handleFile(e.target.files?.[0])}
                     data-testid="intake-file-input" />
            </div>

            {/* Annexes — TOUJOURS visible, même avant de choisir le contrat.
                 UX critique : le user doit savoir DÈS le départ qu'il peut
                 ajouter grilles tarifaires + avenants. */}
            <div className="border-t p-4 space-y-3 bg-slate-50/50" data-testid="intake-annexes-section">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5 text-blue-600" />
                    2️⃣ Documents complémentaires <span className="text-xs font-normal text-slate-500">(optionnel)</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Ajoute la <strong>grille tarifaire</strong>, les <strong>avenants</strong> ou le <strong>devis</strong> si l'info n'est pas dans le contrat.
                    <br/>L'IA les analysera comme contexte pour compléter les tarifs, produits et durées manquants.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm"
                        onClick={() => annexInputRef.current?.click()}
                        className="shrink-0"
                        data-testid="intake-add-annex-btn">
                  <Upload className="h-3.5 w-3.5 mr-1.5" /> Ajouter
                </Button>
                <input type="file" ref={annexInputRef} className="hidden" multiple
                       accept=".pdf,image/*"
                       onChange={(e) => { addAnnexes(e.target.files); e.target.value = ""; }}
                       data-testid="intake-annex-input" />
              </div>
              {annexes.length > 0 ? (
                <div className="space-y-1.5">
                  {annexes.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md border bg-white px-2.5 py-1.5"
                         data-testid={`intake-annex-${i}`}>
                      <FileText className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                      <span className="text-xs flex-1 truncate">{a.name}</span>
                      <span className="text-[10px] text-slate-400">{(a.size / 1024).toFixed(0)} Ko</span>
                      <button onClick={() => removeAnnex(i)}
                              className="text-slate-400 hover:text-red-600"
                              data-testid={`intake-annex-remove-${i}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-slate-400 italic bg-white rounded-md border border-dashed border-slate-200 px-3 py-2">
                  Aucun document complémentaire pour l'instant
                </div>
              )}
            </div>

            {/* CTA Analyser — actif uniquement quand le contrat principal est déposé */}
            <div className="border-t p-3 flex items-center justify-end gap-3 bg-white">
              {file && (
                <div className="text-xs text-slate-500 mr-auto">
                  <span className="font-medium">{annexes.length + 1}</span> fichier(s) prêt(s) à être analysé(s)
                </div>
              )}
              <Button onClick={analyze}
                      disabled={!file}
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="intake-analyze-btn">
                <Sparkles className="h-4 w-4 mr-1.5" />
                {file ? `Analyser ${annexes.length > 0 ? `(${annexes.length + 1} fichiers)` : "le contrat"}` : "Dépose d'abord un contrat"}
              </Button>
            </div>
          </Card>

          {/* Bandeau features */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
            <FeatureCard icon={FileText} label="Extraction totale" desc="Parties, dates, montants, indexation" />
            <FeatureCard icon={ScrollText} label="Résumé exécutif" desc="TL;DR en 3 phrases" />
            <FeatureCard icon={ShieldAlert} label="5 risques légaux" desc="Chatel, RGPD, L442-1…" />
            <FeatureCard icon={CheckCircle2} label="Score risque 0-100" desc="Priorise ton attention" />
          </div>
        </motion.div>
      )}

      {/* ── PROGRESS ── */}
      {phase === "uploading" && (
        <Card className="p-12 text-center">
          <Loader2 className="h-10 w-10 mx-auto text-blue-500 animate-spin mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-1">
            Analyse en cours…
          </h3>
          <p className="text-sm text-slate-500">
            Gemini lit ton contrat, extrait les clauses, détecte les risques. Environ 15 secondes.
          </p>
        </Card>
      )}

      {/* ── REVIEW EXTRACTED ── */}
      {phase === "extracted" && ext && (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                       className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* PDF preview */}
            <Card className="p-0 overflow-hidden h-[720px]">
              <div className="p-3 border-b bg-slate-50 text-xs font-medium text-slate-600 flex items-center gap-2">
                <FileText className="h-3.5 w-3.5" /> {file?.name}
              </div>
              {previewUrl && (
                <iframe src={previewUrl + "#toolbar=0"} title="PDF" className="w-full h-full border-0"
                        data-testid="intake-pdf-preview" />
              )}
            </Card>

            {/* Extracted data (editable) */}
            <div className="space-y-3">
              {/* TL;DR */}
              <Card className="p-4 border-blue-200 bg-blue-50/40">
                <div className="text-[11px] font-medium uppercase tracking-wide text-blue-700 mb-1 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Résumé exécutif
                </div>
                <Textarea value={ext.summary_tldr || ""}
                          onChange={(e) => setExt({ ...ext, summary_tldr: e.target.value })}
                          className="border-blue-200 bg-white text-sm"
                          rows={3}
                          data-testid="intake-tldr" />
              </Card>

              {/* Risk score + risks */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> Risques légaux
                  </div>
                  <RiskScore score={ext.risk_score || 0} />
                </div>
                {(ext.risks || []).length === 0 ? (
                  <div className="text-sm text-emerald-700 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" /> Aucun risque légal majeur détecté
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ext.risks.map((r, i) => (
                      <RiskItem key={i} risk={r} />
                    ))}
                  </div>
                )}
              </Card>

              {/* Fields */}
              <Card className="p-4 space-y-3">
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-1">
                  Données extraites
                </div>
                {ext.tenant_role && ext.tenant_role !== "unknown" && (
                  <div className="text-[11px] rounded-md border border-blue-200 bg-blue-50 text-blue-900 px-2 py-1.5 flex items-start gap-1.5">
                    <Sparkles className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>
                      Dans ce contrat, <b>vous êtes {ext.tenant_role === "provider" ? "le fournisseur" : "le client"}</b>.
                      La contrepartie ({ext.tenant_role === "provider" ? "le client" : "le fournisseur"}) sera enregistrée dans la fiche du contrat.
                    </span>
                  </div>
                )}
                <Field label="Type de contrat" icon={FileText}>
                  <Input value={TYPE_LABEL[ext.contract_type] || ext.contract_type || ""}
                         onChange={(e) => setExt({ ...ext, contract_type: e.target.value })}
                         className="h-9 text-sm" data-testid="intake-type" />
                </Field>

                {/* 🏷️ Catégorie de service — combobox liste tenant + créer nouveau.
                     Rendu OBLIGATOIRE avant sauvegarde pour ne plus voir
                     "prestation_service" partout dans l'annuaire. */}
                <ServiceCategoryCombobox
                  value={ext.service_category || ""}
                  onChange={(v) => setExt({ ...ext, service_category: v })}
                />

                <div className="grid grid-cols-2 gap-3">
                  <Field label={ext.tenant_role === "client" ? "Fournisseur (contrepartie)" : "Fournisseur"} icon={Building2}>
                    <Input value={ext.party_provider?.name || ""}
                           onChange={(e) => setExt({ ...ext, party_provider: { ...ext.party_provider, name: e.target.value } })}
                           className={cn("h-9 text-sm", ext.tenant_role === "client" && "border-blue-400 ring-1 ring-blue-200")}
                           data-testid="intake-provider" />
                  </Field>
                  <Field label={ext.tenant_role !== "client" ? "Client (contrepartie)" : "Client"} icon={Building2}>
                    <Input value={ext.party_client?.name || ""}
                           onChange={(e) => setExt({ ...ext, party_client: { ...ext.party_client, name: e.target.value } })}
                           className={cn("h-9 text-sm", ext.tenant_role !== "client" && "border-blue-400 ring-1 ring-blue-200")}
                           data-testid="intake-client" />
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Signature" icon={Calendar}>
                    <Input type="date" value={ext.signature_date || ""}
                           onChange={(e) => setExt({ ...ext, signature_date: e.target.value })}
                           className="h-9 text-sm" />
                  </Field>
                  <Field label="Effet" icon={Calendar}>
                    <Input type="date" value={ext.effective_date || ""}
                           onChange={(e) => setExt({ ...ext, effective_date: e.target.value })}
                           className="h-9 text-sm" />
                  </Field>
                  <Field label="Fin" icon={Calendar}>
                    <Input type="date" value={ext.end_date || ""}
                           onChange={(e) => setExt({ ...ext, end_date: e.target.value })}
                           className="h-9 text-sm" data-testid="intake-end-date" />
                  </Field>
                </div>

                {/* 🔑 Durée d'engagement — champ MANQUANT auparavant, cause de la
                     confusion utilisateur qui voyait "12 mois" (reconduction) au
                     lieu de la durée d'engagement réelle. Rendu très visible. */}
                <Field label="Durée d'engagement (mois)" icon={Repeat}>
                  <div className="flex items-center gap-2">
                    <Input type="number"
                           value={ext.duration_months ?? ""}
                           onChange={(e) => {
                             const v = e.target.value === "" ? null : parseInt(e.target.value);
                             setExt({ ...ext, duration_months: Number.isNaN(v) ? null : v });
                           }}
                           className="h-9 text-sm font-semibold text-blue-700 border-blue-200 max-w-[140px]"
                           placeholder="ex: 63"
                           data-testid="intake-duration-months" />
                    <span className="text-xs text-slate-500">
                      {ext.duration_months
                        ? `= ${(ext.duration_months / 12).toFixed(1)} an(s)`
                        : "non détectée"}
                    </span>
                  </div>
                </Field>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Montant HT" icon={Euro}>
                    <Input type="number" step="0.01" value={ext.amount_ht || ""}
                           onChange={(e) => setExt({ ...ext, amount_ht: parseFloat(e.target.value) || 0 })}
                           className="h-9 text-sm" data-testid="intake-amount" />
                  </Field>
                  <Field label="TVA %">
                    <Input type="number" value={ext.vat_rate ?? 20}
                           onChange={(e) => setExt({ ...ext, vat_rate: parseFloat(e.target.value) || 0 })}
                           className="h-9 text-sm" />
                  </Field>
                  <Field label="Cycle">
                    <Select value={normCycle(ext.billing_cycle)}
                            onValueChange={(v) => setExt({ ...ext, billing_cycle: v })}>
                      <SelectTrigger className="h-9 text-sm" data-testid="intake-cycle-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CYCLE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Reconduction tacite" icon={Repeat}>
                    <div className="h-9 flex items-center text-sm">
                      <input id="tacit-renewal-checkbox" name="tacit_renewal" type="checkbox" checked={!!ext.tacit_renewal}
                             onChange={(e) => setExt({ ...ext, tacit_renewal: e.target.checked })}
                             className="mr-2" />
                      {ext.tacit_renewal
                        ? `Oui · par ${ext.tacit_renewal_period_months || 12} mois`
                        : "Non"}
                    </div>
                  </Field>
                  <Field label="Préavis (jours)">
                    <Input type="number" value={ext.notice_period_days || 0}
                           onChange={(e) => setExt({ ...ext, notice_period_days: parseInt(e.target.value) || 0 })}
                           className="h-9 text-sm" />
                  </Field>
                </div>
              </Card>

              {/* Indexation */}
              {ext.indexation?.present && (
                <Card className="p-4 border-indigo-200 bg-indigo-50/40">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-indigo-700 mb-2">
                    Clause d'indexation détectée
                  </div>
                  <div className="text-sm text-slate-800">
                    <b>{ext.indexation.index_name || "Indice non identifié"}</b> —
                    {" "}{ext.indexation.formula || "formule non explicite"}
                  </div>
                </Card>
              )}

              {/* Obligations */}
              {(ext.obligations || []).length > 0 && (
                <Card className="p-4">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-2">
                    Obligations clés
                  </div>
                  <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
                    {ext.obligations.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={reset} data-testid="intake-reset-btn">
                  Recommencer
                </Button>
                <Button onClick={save} disabled={phase === "saving" || !ext.service_category?.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="intake-save-btn"
                        title={!ext.service_category?.trim() ? "Choisis d'abord une catégorie de service" : ""}>
                  {phase === "saving"
                    ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                  {phase === "saving" ? "Création…" : "Créer le contrat"}
                </Button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, label, desc }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-blue-600" strokeWidth={2} />
        <span className="text-xs font-medium text-slate-900">{label}</span>
      </div>
      <p className="text-[11px] text-slate-500">{desc}</p>
    </div>
  );
}

/**
 * ServiceCategoryCombobox — Combobox : liste des catégories déjà utilisées
 * par le tenant + défauts métier + création à la volée.
 * Composant "controlled" : value/onChange gérés par le parent.
 */
function ServiceCategoryCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState(value || "");

  // Sync q with value when parent updates (ex: IA auto-fill)
  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contracts/service-categories");
        setCategories(data.categories || []);
      } catch { /* silent */ }
    })();
  }, []);

  const filtered = categories.filter((c) =>
    !q || c.toLowerCase().includes(q.toLowerCase())
  );
  const canCreate = q.trim().length >= 2
    && !categories.some((c) => c.toLowerCase() === q.trim().toLowerCase());

  const pick = (label) => {
    onChange(label);
    setQ(label);
    setOpen(false);
  };

  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1">
        <FileText className="h-3 w-3" />
        Catégorie de service <span className="text-red-500">*</span>
        <span className="ml-auto text-[10px] font-normal text-slate-400">
          {value ? "" : "requis pour créer le contrat"}
        </span>
      </label>
      <div className="relative">
        <Input value={q}
               onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }}
               onFocus={() => setOpen(true)}
               onBlur={() => setTimeout(() => setOpen(false), 150)}
               placeholder="Ex: Sauvegarde de données, Cybersécurité, Télécom…"
               className={cn(
                 "h-9 text-sm",
                 !value?.trim() && "border-amber-300 bg-amber-50/30"
               )}
               data-testid="intake-service-category" />
        {open && (filtered.length > 0 || canCreate) && (
          <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg">
            {canCreate && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(q.trim()); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b flex items-center gap-2 text-blue-700"
                data-testid="intake-service-category-create">
                <Sparkles className="h-3 w-3" />
                Créer <b>« {q.trim()} »</b>
              </button>
            )}
            {filtered.map((c) => (
              <button
                key={c}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(c); }}
                className={cn(
                  "w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50",
                  value === c && "bg-blue-50 text-blue-700 font-medium"
                )}
                data-testid={`intake-service-category-item-${c}`}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-500 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function RiskScore({ score }) {
  const tone = score >= 80 ? "critical" : score >= 51 ? "high" : score >= 21 ? "medium" : "low";
  const toneCls = {
    low: "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: "bg-amber-50 text-amber-800 border-amber-200",
    high: "bg-orange-50 text-orange-700 border-orange-200",
    critical: "bg-red-50 text-red-700 border-red-200",
  }[tone];
  const label = { low: "Sain", medium: "À surveiller", high: "Risqué", critical: "Critique" }[tone];
  return (
    <div className={cn("inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium",
                       toneCls)}
         data-testid="intake-risk-score">
      <span className="tabular-nums font-semibold">{score}</span>
      <span>· {label}</span>
    </div>
  );
}

function RiskItem({ risk }) {
  const meta = RISK_META[risk.code] || { label: risk.code, tone: "medium" };
  const tone = risk.severity || meta.tone;
  const cls = {
    low: "border-emerald-200 bg-emerald-50/40",
    medium: "border-amber-200 bg-amber-50/40",
    high: "border-red-200 bg-red-50/40",
  }[tone] || "border-slate-200 bg-slate-50";
  return (
    <div className={cn("rounded-lg border p-2", cls)}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-700 shrink-0" />
        <div className="flex-1">
          <div className="text-xs font-medium text-slate-900">{meta.label}</div>
          {risk.explanation && (
            <div className="text-[11px] text-slate-600 mt-0.5">{risk.explanation}</div>
          )}
          {risk.quote && (
            <blockquote className="text-[11px] text-slate-500 italic mt-1 border-l-2 border-slate-300 pl-2">
              « {risk.quote} »
            </blockquote>
          )}
        </div>
        <Badge variant="outline" className="text-[9px] uppercase shrink-0">{tone}</Badge>
      </div>
    </div>
  );
}
